import { useAtomValue } from "jotai";
import { Check } from "lucide-react";
import { cn } from "tailwind-variants";

import { refineArtworkOpenAtom } from "@design-lab/entities/artwork";
import { useFormattedDeliveryDate } from "@design-lab/entities/composition/model";
import { hasRequestedDesignServicesAtom, pinningModeAtom } from "@design-lab/entities/pinning";
import { DesignArtwork } from "@design-lab/features/design-artwork";
import { ReviewPinningModal } from "@design-lab/features/manage-cart";
import { ManageProductSizing } from "@design-lab/features/manage-product-sizing/ui/ManageProductSizing";
import { SaveProject } from "@design-lab/features/save-project";
import { useSaveProject } from "@design-lab/features/save-project/hooks/useSaveProject";
import { SelectProductColor } from "@design-lab/features/select-product-color";
import {
  useRecapture,
  UserRetentionModal,
  useUserRetention
} from "@design-lab/features/user-retention";
import { useValidation } from "@design-lab/shared/hooks/useValidation";
import type { AppTab, Tab } from "@design-lab/shared/types/tab";
import { FrontBackSubTabs } from "@design-lab/shared/ui/layouts/FrontBackSubTabs";
import { UploadSection } from "@design-lab/shared/ui/layouts/UploadSection";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@design-lab/shared/ui/tabs";
import { DesignLabArtboard, FooterDesktop } from "@design-lab/widgets";
import { PinningArtboard, PinningPanel } from "@design-lab/widgets/artboard/ui/pinning-mode";
import { Header } from "@design-lab/widgets/navigation";

export function DesignLabDesktop({
  handleTabClick,
  tabs,
  tab,
  setTab
}: {
  handleTabClick: (value: Tab) => void;
  tabs: AppTab[];
  tab: Tab;
  setTab: (value: Tab) => void;
}) {
  const deliveryDateInfo = useFormattedDeliveryDate();
  const isRefineOpen = useAtomValue(refineArtworkOpenAtom);
  const isPinningMode = useAtomValue(pinningModeAtom);
  const hasRequestedDesignServices = useAtomValue(hasRequestedDesignServicesAtom);
  const { isSaveProjectVisible, setIsSaveProjectVisible } = useSaveProject();
  const { isArtworkEnhanced, isSomeArtworkLoading } = useValidation();
  const { onRecaptured } = useRecapture({ setTab, isSaveProjectVisible, setIsSaveProjectVisible });

  const { isModalOpen, closeModal, handleRecaptured } = useUserRetention({
    hasArtwork: isArtworkEnhanced,
    isSomeArtworkLoading,
    hasRequestedDesignServices,
    onRecaptured,
    enabled: !isPinningMode && !isRefineOpen
  });

  return (
    <div className="flex h-dvh flex-col overflow-hidden bg-gray-100">
      <Header deliveryDateInfo={deliveryDateInfo} />
      <UserRetentionModal
        isOpen={isModalOpen}
        onClose={closeModal}
        onRequestEdits={handleRecaptured}
      />
      <ReviewPinningModal />
      {isSaveProjectVisible ? (
        <SaveProject artboardPreview={<DesignLabArtboard readonly={true} />} />
      ) : (
        <>
          <div className="flex flex-1 gap-4 overflow-hidden p-6">
            {isPinningMode ? (
              <PinningPanel />
            ) : (
              <Tabs
                value={tab}
                onValueChange={value => handleTabClick(value as Tab)}
                className={
                  cn(
                    "flex max-w-[700px] flex-1 flex-row items-start gap-4",
                    isRefineOpen && "pointer-events-none opacity-50"
                  )!
                }
              >
                <TabsList
                  data-testid="design-lab-main-tablist"
                  className="flex-0 h-auto flex-col gap-2"
                >
                  {tabs.map((item, index) => (
                    <TabsTrigger
                      data-testid={`tab-${item.value}-trigger`}
                      key={item.title}
                      value={item.value}
                      disabled={!item.enabled}
                      className="bg-background border-3 text-foreground relative h-auto min-h-20 w-full flex-col gap-2 rounded-2xl px-3 py-2 text-sm shadow-md outline outline-offset-[-1px] outline-gray-200 data-[selected]:border-blue-600 data-[selected]:text-blue-600"
                    >
                      {item.icon}
                      {item.enabled && item.checked && (
                        <div className="absolute right-2 top-2 flex size-5 items-center justify-center rounded-full bg-blue-600">
                          <Check className="text-background h-3 w-3" />
                        </div>
                      )}
                      <span>
                        {index + 1}. {item.title}
                      </span>
                    </TabsTrigger>
                  ))}
                </TabsList>
                <div className="h-full flex-1 overflow-hidden">
                  <TabsContent value="upload" className="mt-0 h-full">
                    <UploadSection />
                  </TabsContent>
                  <TabsContent value="products" className="mt-0 h-full">
                    <SelectProductColor />
                  </TabsContent>
                  <TabsContent value="size" className="mt-0 h-full">
                    <ManageProductSizing />
                  </TabsContent>
                  <TabsContent value="design" className="mt-0 h-full">
                    <FrontBackSubTabs
                      title="Design"
                      number={2}
                      testIdPrefix="design"
                      frontContent={<DesignArtwork side="front" title="" />}
                      backContent={<DesignArtwork side="back" title="" />}
                    />
                  </TabsContent>
                </div>
              </Tabs>
            )}
            <div className="flex-1">
              {isPinningMode ? <PinningArtboard /> : <DesignLabArtboard />}
            </div>
          </div>
          <FooterDesktop />
        </>
      )}
    </div>
  );
}
